import React, { useCallback, useState } from 'react';
import styled from 'astroturf/react';

import routes from 'js/core_pre_webpack/routes';
import { Body } from 'js/design_system/Body';
import { Button } from 'js/design_system/Button';
import { Icon } from 'js/design_system/Icon';

import { WorkSearchResult } from './resultToFormState';
import { Typeahead } from './Typeahead';

const SearchWrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--spacing-xs-16, 16px);
  width: 100%;
  padding-top: var(--spacing-xs-16, 16px);
`;

const SearchIcon = styled.span`
  display: flex;
  align-items: center;
  padding-left: var(--spacing-xs-12, 12px);
  color: var(--icon-grey, #b1b1ba);
`;

const Option = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--type-paragraph-spacings-4, 4px);
  width: 100%;
  padding: var(--spacing-xs-8, 8px) 0;
  text-align: left;
`;

const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--spacing-xs-12, 12px);
  padding: var(--spacing-xs-16, 16px);
`;

const MIN_QUERY_LENGTH = 3;

type PapersSearchProps = {
  onSelect: (result: WorkSearchResult) => void;
  onEnterManually: (query: string) => void;
};

function formatAuthors(result: WorkSearchResult): string {
  return result.authors
    .map((author) => [author.given, author.family].filter(Boolean).join(' '))
    .filter((name) => name !== '')
    .join(', ');
}

function formatMeta(result: WorkSearchResult): string {
  return [result.venue, result.year != null ? String(result.year) : '']
    .filter((part) => part !== '')
    .join(', ');
}

async function searchWorks(query: string): Promise<WorkSearchResult[]> {
  const path = routes.citation_generator_works_search_path({ q: query });
  const response = await fetch(path, {
    headers: { Accept: 'application/json' },
  });
  if (!response.ok) {
    throw new Error(`works_search failed: ${response.status}`);
  }
  const payload = (await response.json()) as { results?: WorkSearchResult[] };
  return Array.isArray(payload.results) ? payload.results : [];
}

export function PapersSearch({
  onSelect,
  onEnterManually,
}: PapersSearchProps): React.ReactElement {
  const [query, setQuery] = useState('');

  const onSearch = useCallback(
    async (search: string): Promise<WorkSearchResult[]> => {
      const trimmed = search.trim();
      if (trimmed.length < MIN_QUERY_LENGTH) return [];
      try {
        return await searchWorks(trimmed);
      } catch {
        return [];
      }
    },
    [],
  );

  const renderEmpty = useCallback(
    (search: string) => {
      if (search.trim().length < MIN_QUERY_LENGTH) return null;
      return (
        <EmptyState>
          <Body size="sm">No matches — enter the details manually</Body>
          <div>
            <Button
              type="button"
              size="sm"
              secondary
              onMouseDown={(event) => event.preventDefault()}
              onClick={() => onEnterManually(search.trim())}
            >
              Enter the details manually
            </Button>
          </div>
        </EmptyState>
      );
    },
    [onEnterManually],
  );

  const renderOption = useCallback((result: WorkSearchResult) => {
    const authors = formatAuthors(result);
    const meta = formatMeta(result);
    return (
      <Option>
        <Body size="sm" bold as="span">
          {result.title}
        </Body>
        {authors !== '' && (
          <Body size="xs" as="span">
            {authors}
          </Body>
        )}
        {meta !== '' && (
          <Body size="xs" as="span">
            {meta}
          </Body>
        )}
        {result.source === 'openalex' && (
          <Body size="xs" as="span">
            From OpenAlex
          </Body>
        )}
      </Option>
    );
  }, []);

  return (
    <SearchWrap>
      <Typeahead<WorkSearchResult>
        label="Search papers"
        placeholder="Search 55M+ papers by title, author, or DOI"
        prepend={
          <SearchIcon>
            <Icon name="search" size={20} />
          </SearchIcon>
        }
        hideEmpty={query.trim().length < MIN_QUERY_LENGTH}
        keyExtractor={(result) => result.id}
        renderOption={renderOption}
        renderEmpty={renderEmpty}
        onSearch={onSearch}
        onSelect={onSelect}
        onInputChange={setQuery}
      />
    </SearchWrap>
  );
}
